import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Autocomplete,
  createFilterOptions,
  IconButton,
  MenuItem,
  Popper,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useCallback, useEffect, useState } from "react";
import {
  FlexVerticalAligned,
  SelectLight,
  TextFieldLight,
} from "../../../common/components/styled/styled";
import { ReactComponent as DeleteIcon } from "../../../common/icons/delete.svg";
import { CustomerCostCenter } from "../../customer/customerAPI";
import { SalesAccount } from "../../settings/settingsApi";
import { EezyCostCenter, InvoiceRow, TYPE_EXTERNAL } from "../invoiceAPI";
import { TableCellBorderless } from "../styled";
import { InvoiceRowActionsMenu } from "./InvoiceRowActionsMenu";
import formatNumber from "../../../common/helpers/formatNumber";
import { VAT_CODES_ARRAY, VAT_PERCENTS } from "../constants";
import { calculateInvoiceRowAmounts } from "../utils";
import { NumericFormat } from "react-number-format";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";

interface Props {
  row: InvoiceRow;
  customerOrgId: string | undefined;
  onDelete: (rowId: string) => void;
  onUpdate: (row: InvoiceRow) => void;
  onCopy: (rowId: string) => void;
  invoiceType: string;
  customerCCs: CustomerCostCenter[];
  eezyBuyerCCs: EezyCostCenter[];
  sellerCCs: EezyCostCenter[];
  salesAccounts: SalesAccount[];
  actionsMenuDisabled: boolean;
}

const CustomInput = ({ ...props }) => (
  <TextFieldLight {...props} size="small" />
);

export const InvoiceContentRow = ({
  row,
  customerOrgId,
  onDelete,
  onUpdate,
  onCopy,
  invoiceType,
  customerCCs: customerCCOptions,
  eezyBuyerCCs: eezyBuyerCCOptions,
  sellerCCs: sellerCCOptions,
  salesAccounts: salesAccountOptions,
  actionsMenuDisabled,
}: Props) => {
  const { t } = useTranslation("invoices");
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [displayFreeText, setDisplayFreeText] = useState<boolean>(
    !!row.freeText,
  );

  const freeTextSx = !!displayFreeText
    ? { display: "contents" }
    : { display: "none" };

  const getDefaultVatValues = useCallback((): {
    vatPercent: string;
    vatCode: string;
  } => {
    const salesAccount: SalesAccount | undefined = salesAccountOptions.find(
      (sa) => sa.code === row.salesAccountCode,
    );
    if (!salesAccount) return { vatPercent: "0.00", vatCode: "" };
    else {
      return {
        vatPercent: parseFloat(salesAccount.defaultVatPercent).toString(),
        vatCode: salesAccount.defaultVatCode,
      };
    }
  }, [salesAccountOptions, row.salesAccountCode]);

  const [articleName, setArticleName] = useState<string>(row.articleName || "");
  const [articleCategory, setArticleCategory] = useState<string>(
    row.articleCategory || "",
  );
  const [sellerCostCenterId, setSellerCostCenterId] = useState<string>(
    row.eezyCostCenterId,
  );
  const [salesAccount, setSalesAccount] = useState<string>(
    row.salesAccountCode,
  );
  const [quantityAmount, setQuantityAmount] = useState<string>(
    row.quantityAmount || "0",
  );
  const [quantityUnit, setQuantityUnit] = useState<string>(row.quantityUnit);
  const [unitPriceAmount, setUnitPriceAmount] = useState<string>(
    row.unitPriceAmount || "0",
  );
  const [customerCCId, setCustomerCCId] = useState<string>(
    row.customerCostCenterId || "",
  );
  const [eezyBuyerCCId, setEezyBuyerCCId] = useState<string>(
    row.eezyBuyerCostCenterId || "",
  );
  const [discountPercent, setDiscountPercent] = useState<number>(
    parseFloat(row.discountPercent ?? "") || 0,
  );
  const [vatPercent, setVatPercent] = useState<string>(
    parseFloat(row.vatRatePercent).toString(),
  );
  const [vatCode, setVatCode] = useState<string>(row.vatCode);
  const [vatExcludedAmount, setVatExcludedAmount] = useState<number>(
    parseFloat(row.vatExcludedAmount) || 0,
  );
  const [vatAmount, setVatAmount] = useState<number>(
    parseFloat(row.vatAmount) || 0,
  );
  const [sum, setSum] = useState<number>(parseFloat(row.amount) || 0);
  const [freeText, setFreeText] = useState<string>(row.freeText || "");

  const selectSellerCostCenter = (cc: EezyCostCenter) => {
    setSellerCostCenterId(cc.eezyCostCenterId);
  };

  const selectSalesAccount = (account: SalesAccount) => {
    setSalesAccount(account.code);

    // Set the default vat percent and vat code
    setVatPercent(account.defaultVatPercent);
    setVatCode(account.defaultVatCode);
  };

  const selectCustomerCostCenter = (customerCC: CustomerCostCenter) => {
    setCustomerCCId(customerCC.id);
  };

  const selectEezyBuyerCostCenter = (eezyCC: EezyCostCenter) => {
    setEezyBuyerCCId(eezyCC.eezyCostCenterId);
  };

  const selectVatPercent = (event: any) => {
    setVatPercent(event.target.value);
  };

  const selectVatCode = (event: any) => {
    setVatCode(event.target.value);
  };

  const addFreeText = () => {
    setDisplayFreeText(true);
  };

  const removeFreeText = () => {
    setDisplayFreeText(false);
    setFreeText("");
  };

  // Get default VAT percent and code for the row when initializing new invoice.
  // The values need to be gotten from the sales account data instead of
  // contract for them to be up to date.
  // Note: do this only for new invoices, hence version check
  useEffect(() => {
    if (!salesAccountOptions.length || row.version) return;
    const { vatPercent, vatCode } = getDefaultVatValues();
    setVatPercent(vatPercent);
    setVatCode(vatCode);
  }, [salesAccountOptions, getDefaultVatValues, row.version]);

  useEffect(() => {
    if (row.editable) {
      const amounts = calculateInvoiceRowAmounts({
        quantity: parseFloat(quantityAmount),
        unitPrice: parseFloat(unitPriceAmount),
        discountPercent,
        vatPercent: parseFloat(vatPercent),
      });
      setVatAmount(amounts.vatAmount);
      setVatExcludedAmount(amounts.vatExcludedAmount);
      setSum(amounts.amount);
    }
  }, [
    quantityAmount,
    unitPriceAmount,
    discountPercent,
    vatPercent,
    row.editable,
  ]);

  useEffect(() => {
    let rowObject: InvoiceRow = {
      id: row.id,
      articleName,
      articleCategory,
      eezyCostCenterId: sellerCostCenterId,
      salesAccountCode: salesAccount,
      quantityAmount,
      quantityUnit,
      unitPriceAmount: unitPriceAmount.toString(),
      discountPercent: discountPercent.toString(),
      vatRatePercent: vatPercent,
      vatCode,
      vatExcludedAmount: vatExcludedAmount.toString(),
      vatAmount: vatAmount.toString(),
      amount: sum.toString(),
      freeText,
      editable: row.editable,
      version: row.version,
    };
    if (invoiceType === TYPE_EXTERNAL) {
      rowObject = {
        ...rowObject,
        customerOrganizationId: customerOrgId,
        customerCostCenterId: customerCCId,
      };
    } else {
      rowObject = {
        ...rowObject,
        eezyBuyerCostCenterId: eezyBuyerCCId,
      };
    }
    onUpdate(rowObject);
  }, [
    articleCategory,
    articleName,
    sellerCostCenterId,
    salesAccount,
    quantityAmount,
    quantityUnit,
    unitPriceAmount,
    customerCCId,
    eezyBuyerCCId,
    discountPercent,
    vatPercent,
    vatCode,
    vatExcludedAmount,
    vatAmount,
    freeText,
    customerOrgId,
    invoiceType,
    sum,
    row.editable,
    row.id,
    row.version,
    onUpdate,
  ]);

  return (
    <>
      <TableRow>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              minWidth: "100px",
            }}
            size="small"
            value={articleName}
            disabled={!row.editable}
            onChange={(event) => setArticleName(event.target.value)}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              minWidth: "100px",
            }}
            size="small"
            value={articleCategory}
            disabled={!row.editable}
            onChange={(event) => setArticleCategory(event.target.value)}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <Autocomplete
            value={
              sellerCCOptions.find(
                (cc) => cc.eezyCostCenterId === sellerCostCenterId,
              )!!
            }
            disabled={!row.editable}
            disableClearable
            size="small"
            sx={{
              // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
              "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                { minWidth: "30px" },
              width: "100px",
            }}
            PopperComponent={(props) => (
              <Popper {...props} style={{ width: 350 }} />
            )}
            options={sellerCCOptions}
            filterOptions={createFilterOptions({
              stringify: (option) => JSON.stringify(option),
            })}
            getOptionLabel={(option) => option.code}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.eezyCostCenterId}
                value={option.eezyCostCenterId}
              >
                {`${option.code} ${option.name}`}
              </li>
            )}
            onChange={(_, newValue) => selectSellerCostCenter(newValue)}
            renderInput={(params) => <CommonTextField {...params} />}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <Autocomplete
            value={salesAccountOptions.find((sa) => sa.code === salesAccount)!!}
            disabled={!row.editable}
            disableClearable
            size="small"
            sx={{
              // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
              "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                { minWidth: "30px" },
              width: "110px",
            }}
            PopperComponent={(props) => (
              <Popper {...props} style={{ width: 350 }} />
            )}
            options={salesAccountOptions}
            filterOptions={createFilterOptions({
              stringify: (option) => JSON.stringify(option),
            })}
            getOptionLabel={(option) => option.code}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderOption={(props, option) => (
              <li {...props} key={option.code} value={option.code}>
                {option.code} {option.description}
              </li>
            )}
            onChange={(_, newValue) => selectSalesAccount(newValue)}
            renderInput={(params) => <CommonTextField {...params} />}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <NumericFormat
            value={quantityAmount}
            onValueChange={(values) => setQuantityAmount(values.value)}
            error={Number.isNaN(parseFloat(quantityAmount))}
            disabled={!row.editable}
            decimalSeparator=","
            decimalScale={2}
            valueIsNumericString
            customInput={CustomInput}
            sx={{
              width: "90px",
            }}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              width: "70px",
            }}
            size="small"
            value={quantityUnit}
            disabled={!row.editable}
            onChange={(event) => setQuantityUnit(event.target.value)}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <NumericFormat
            value={unitPriceAmount}
            onValueChange={(values) => setUnitPriceAmount(values.value)}
            error={Number.isNaN(parseFloat(unitPriceAmount))}
            disabled={!row.editable}
            decimalSeparator=","
            decimalScale={2}
            valueIsNumericString
            customInput={CustomInput}
            sx={{
              width: "100px",
            }}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          {invoiceType === TYPE_EXTERNAL ? (
            <Autocomplete
              value={customerCCOptions.find((cc) => cc.id === customerCCId)}
              disabled={!row.editable}
              disableClearable
              size="small"
              sx={{
                // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
                "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                  { minWidth: "30px" },
                width: "100px",
              }}
              PopperComponent={(props) => (
                <Popper {...props} style={{ width: 350 }} />
              )}
              options={customerCCOptions}
              filterOptions={createFilterOptions({
                stringify: (option) => JSON.stringify(option),
              })}
              getOptionLabel={(option) => option.code || option.name}
              isOptionEqualToValue={(option, value) =>
                option.code
                  ? option.code === value.code
                  : option.id === value.id
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id} value={option.id}>
                  {option.code} {option.name}
                </li>
              )}
              onChange={(_, newValue) => selectCustomerCostCenter(newValue)}
              renderInput={(params) => <CommonTextField {...params} />}
            />
          ) : (
            <Autocomplete
              value={
                eezyBuyerCCOptions.find((cc) => cc.code === eezyBuyerCCId)!!
              }
              disabled={!row.editable}
              disableClearable
              size="small"
              sx={{
                // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
                "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
                  { minWidth: "30px" },
                width: "100px",
              }}
              PopperComponent={(props) => (
                <Popper {...props} style={{ width: 350 }} />
              )}
              options={eezyBuyerCCOptions}
              filterOptions={createFilterOptions({
                stringify: (option) => JSON.stringify(option),
              })}
              getOptionLabel={(option) => option.code}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.eezyCostCenterId}
                  value={option.eezyCostCenterId}
                >
                  {option.code} {option.name}
                </li>
              )}
              onChange={(_, newValue) => selectEezyBuyerCostCenter(newValue)}
              renderInput={(params) => <CommonTextField {...params} />}
            />
          )}
        </TableCellBorderless>
        <TableCellBorderless>
          <NumericFormat
            value={discountPercent}
            onValueChange={(values) =>
              setDiscountPercent(values.floatValue ?? 0)
            }
            isAllowed={({ floatValue }) =>
              floatValue === undefined || floatValue <= 100
            }
            disabled={!row.editable}
            decimalSeparator=","
            decimalScale={2}
            allowNegative={false}
            customInput={CustomInput}
            sx={{
              width: "60px",
            }}
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <SelectLight
            size="small"
            value={vatPercent}
            disabled={!row.editable}
            onChange={selectVatPercent}
          >
            {VAT_PERCENTS.map((percent) => (
              <MenuItem key={percent} value={percent}>
                {percent}
              </MenuItem>
            ))}
          </SelectLight>
        </TableCellBorderless>
        <TableCellBorderless>
          <SelectLight
            size="small"
            value={vatCode}
            disabled={!row.editable}
            onChange={selectVatCode}
          >
            {VAT_CODES_ARRAY.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </SelectLight>
        </TableCellBorderless>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              width: "120px",
            }}
            size="small"
            value={formatNumber(vatExcludedAmount.toString())}
            disabled
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              width: "120px",
            }}
            size="small"
            value={formatNumber(vatAmount.toString())}
            disabled
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <TextFieldLight
            sx={{
              width: "120px",
            }}
            size="small"
            value={formatNumber(sum.toString())}
            disabled
          />
        </TableCellBorderless>
        <TableCellBorderless>
          <IconButton
            sx={{ width: "32px", height: "32px" }}
            onClick={(e) => setActionMenuAnchor(e.currentTarget)}
            disabled={actionsMenuDisabled}
          >
            <MoreVertIcon />
          </IconButton>
        </TableCellBorderless>
        <InvoiceRowActionsMenu
          open={!!actionMenuAnchor}
          anchorEl={actionMenuAnchor}
          onClose={() => setActionMenuAnchor(null)}
          rowId={row.id}
          isRowEditable={row.editable}
          onAddFreeText={addFreeText}
          onDeleteRow={onDelete}
          onCopyRow={onCopy}
        />
      </TableRow>
      <TableRow sx={freeTextSx}>
        <TableCellBorderless colSpan={5} />
        <TableCellBorderless colSpan={2} sx={{ textAlign: "right" }}>
          <Typography fontSize="14px" marginTop="-22px">
            {t("freeText")}
          </Typography>
        </TableCellBorderless>
        <TableCellBorderless colSpan={7}>
          <FlexVerticalAligned>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              value={freeText}
              disabled={!row.editable}
              onChange={(event) => setFreeText(event.target.value)}
              maxRows={3}
              multiline
              error={freeText.length > 512}
              helperText={
                <Typography fontSize="12px" textAlign={"right"}>
                  {freeText?.length || 0}/{512}
                </Typography>
              }
            />
          </FlexVerticalAligned>
        </TableCellBorderless>
        <TableCellBorderless>
          <IconButton
            onClick={removeFreeText}
            disabled={!row.editable}
            sx={{
              marginTop: "-24px",
              "&.Mui-disabled path": {
                stroke: (theme) => theme.palette.text.disabled,
                opacity: 0.8,
                strokeWidth: 1.5,
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </TableCellBorderless>
        <TableCellBorderless />
      </TableRow>
    </>
  );
};
